/**
 * NOVA routines: the kinds DCM provides, their schedules and the pure
 * schedule math the runner and the screens share.
 *
 * A routine runs once a week on her weekday at her local time, in her time
 * zone. The kinds live in code: a coach turns a routine on, pauses it or
 * changes its day, but never writes one. Pure and universal.
 *
 * Aliases: nova routines, routine schedule, weekly routine, routine kinds
 */

import { fromZonedTime } from "date-fns-tz";
import { z } from "zod";

import { isWeekday, WEEKDAY_NAMES, type Weekday } from "../call-schedules";

import { WEEKLY_CHECK_IN_PATH } from "./weekly-check-in";

export const NOVA_ROUTINE_KINDS = ["weekly_check_in"] as const;

export type NovaRoutineKind = (typeof NOVA_ROUTINE_KINDS)[number];

/**
 * `paused` is her choice; `paused_inactive` is the runner's, after two missed
 * runs in a row, and opening NOVA turns it back on; `removed` is never
 * recreated.
 */
export const NOVA_ROUTINE_STATUSES = [
  "on",
  "paused",
  "paused_inactive",
  "removed",
] as const;

export type NovaRoutineStatus = (typeof NOVA_ROUTINE_STATUSES)[number];

export const NOVA_ROUTINE_RUN_STATUSES = [
  "open",
  "completed",
  "missed",
] as const;

export type NovaRoutineRunStatus = (typeof NOVA_ROUTINE_RUN_STATUSES)[number];

/** "08:00": 24-hour local time. */
export const routineLocalTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time such as 08:00");

export const routineWeekdaySchema = z.custom<Weekday>(
  (value) => typeof value === "number" && isWeekday(value),
  "Pick a day of the week",
);

export interface RoutineSchedule {
  /** 0 is Sunday, as `Date.getDay()` counts. */
  readonly weekday: Weekday;
  readonly localTime: string;
}

/**
 * The zone a routine runs in until she saves one: New York, where DCM holds
 * its office hours.
 */
export const ROUTINE_FALLBACK_TIME_ZONE = "America/New_York";

export interface NovaRoutineKindInfo {
  readonly title: string;
  /** One sentence on what the routine does, for the Routines page. */
  readonly summary: string;
  /** What it asks of her, such as "about 3 minutes a week". */
  readonly effort: string;
  /** Where she does a run of it. */
  readonly runPath: string;
  readonly defaultSchedule: RoutineSchedule;
}

export const NOVA_ROUTINE_KIND_INFO = {
  weekly_check_in: {
    title: "Weekly check-in",
    summary:
      "Every week NOVA asks how your move went and suggests one move for the next week.",
    effort: "about 3 minutes a week",
    runPath: WEEKLY_CHECK_IN_PATH,
    defaultSchedule: { weekday: 1, localTime: "08:00" },
  },
} as const satisfies Record<NovaRoutineKind, NovaRoutineKindInfo>;

interface CalendarDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** The calendar date `at` falls on in `timeZone`, with its weekday. */
function zonedDate(
  at: Date,
  timeZone: string,
): CalendarDate & { weekday: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((entry) => entry.type === type)?.value);
  const year = part("year");
  const month = part("month");
  const day = part("day");
  // The weekday of a calendar date does not depend on any zone.
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, weekday };
}

/**
 * The instant of `localTime` on the calendar date `dayOffset` days after
 * `date`, in `timeZone`. Days are added on the calendar, never as 24 hours,
 * so 08:00 stays 08:00 across a daylight-saving change.
 */
function instantOn(
  date: CalendarDate,
  dayOffset: number,
  localTime: string,
  timeZone: string,
): Date {
  const target = new Date(
    Date.UTC(date.year, date.month - 1, date.day + dayOffset),
  );
  const isoDate = target.toISOString().slice(0, 10);
  return fromZonedTime(`${isoDate}T${localTime}:00`, timeZone);
}

/** The first time the schedule falls strictly after `after`. */
export function nextRoutineRunAfter(
  after: Date,
  schedule: RoutineSchedule,
  timeZone: string,
): Date {
  const today = zonedDate(after, timeZone);
  const daysAhead = (schedule.weekday - today.weekday + 7) % 7;
  const candidate = instantOn(today, daysAhead, schedule.localTime, timeZone);
  if (candidate.getTime() > after.getTime()) return candidate;
  return instantOn(today, daysAhead + 7, schedule.localTime, timeZone);
}

/** The last time the schedule fell at or before `at`. */
export function latestRoutineRunAtOrBefore(
  at: Date,
  schedule: RoutineSchedule,
  timeZone: string,
): Date {
  const today = zonedDate(at, timeZone);
  const daysBack = (today.weekday - schedule.weekday + 7) % 7;
  const candidate = instantOn(today, -daysBack, schedule.localTime, timeZone);
  if (candidate.getTime() <= at.getTime()) return candidate;
  return instantOn(today, -daysBack - 7, schedule.localTime, timeZone);
}

/** "8:00am". */
export function formatRoutineTime(localTime: string): string {
  const [hour = 0, minute = 0] = localTime.split(":").map(Number);
  const period = hour < 12 ? "am" : "pm";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${String(minute).padStart(2, "0")}${period}`;
}

/** "Monday at 8:00am". */
export function describeRoutineSchedule(schedule: RoutineSchedule): string {
  return `${WEEKDAY_NAMES[schedule.weekday]} at ${formatRoutineTime(schedule.localTime)}`;
}
